<template>
  <div
    v-show="isVisible"
    class="wt-text-overlay"
    :tabindex="tabIndex"
    :style="wrapperStyleObject"
    :class="wrapperClassObject"
    @click="onClick"
    @dblclick="onDoubleClick"
    @keydown="moveTextOverlay"
    @keyup="onKeyUp"
    @input="refreshContainment"
    @cut="onCut"
    @copy="onCopy"
    @paste="onPaste"
  >
    <div class="wt-text-overlay-tooltip" v-show="isActive">
      <div
        v-show="textOverlays.length > 1 && !indicationActive"
        title="Send to Back"
        @click="clickedSendToBack"
        class="wt-text-overlay-tooltip-icon wt-send-to-back"
        :class="{ '-disabled': this.sendToBackDisabled }"
      >
        <i class="wticon wticon-backward" />
      </div>
      <div
        v-show="textOverlays.length > 1 && !indicationActive"
        title="Bring to Front"
        @click="clickedBringToFront"
        class="wt-text-overlay-tooltip-icon wt-bring-to-front"
        :class="{ '-disabled': this.bringToFrontDisabled }"
      >
        <i class="wticon wticon-forward" />
      </div>
      <div
        v-show="!indicationActive"
        class="wt-text-overlay-tooltip-icon wt-copy-text-overlay"
        title="Copy"
        @click="copyManually"
      >
        <i class="wticon wticon-copy" />
      </div>
      <div
        v-show="!indicationActive"
        @click="deleteTextOverlay(textOverlay)"
        class="wt-text-overlay-tooltip-icon wt-delete-text-overlay"
        title="Delete"
      >
        <i class="wticon wticon-trash2" />
      </div>
      <div
        v-show="indicationActive"
        class="wt-indication"
        v-text="indicationText"
      />
    </div>

    <div class="wt-to-xy">
      <div>dx: <span class="dx" v-text="drag.xText" /></div>
      <div>dy: <span class="dy" v-text="drag.yText" /></div>
    </div>
    <div
      v-show="mode === 'editing'"
      class="wt-text-overlay-editor"
      :id="textOverlay.id"
      ref="editor"
      :style="editorStyleObject"
    />
    <div class="wt-drag-handle top" />
    <div class="wt-drag-handle bottom" />
    <div class="wt-drag-handle left" />
    <div class="wt-drag-handle right" />
    <div
      v-show="mode !== 'editing'"
      class="wt-text-overlay-editor-display"
      ref="display"
      :style="editorStyleObject"
    />
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's text overlay stage Vue (stage bar)
 */
import app from 'app';
import Analytics from 'c/WtAnalytics';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import 'rotatable';
// eslint-disable-next-line no-unused-vars
import MceGradientComponent from 'AssetEditor/functionality/TextOverlay/MceGradientComponent.vue';
import { mapGetters, mapMutations } from 'vuex';

function paddingStr(t, r, b, l) {
  t = t ? app.sfp(t, 1e3) : 0;
  r = r ? app.sfp(r, 1e3) : 0;
  b = b ? app.sfp(b, 1e3) : 0;
  l = l ? app.sfp(l, 1e3) : 0;
  if (arguments.length === 1) {
    return '{0}px'.format(t);
  }
  if (arguments.length === 2) {
    return '{0}px {1}px'.format(t, r);
  } else {
    return '{0}px {1}px {2}px {3}px'.format(t, r, b, l);
  }
}

function updateLtrRtlButtons(fontName, editor) {
  try {
    const font = app.fontController.findByName(fontName);
    const atLTR = editor.toolbarFind(
      '.mce-btn[aria-label="Left to right"][aria-pressed="true"]'
    ).length;
    const atRTL = editor.toolbarFind(
      '.mce-btn[aria-label="Right to left"][aria-pressed="true"]'
    ).length;
    if (font.Regular.rtlDirection && !atRTL) {
      editor.execCommand('mceDirectionRTL', false);
    } else if (!font.Regular.rtlDirection && !atLTR) {
      editor.execCommand('mceDirectionLTR', false);
    }
  } catch (bug) {
    app.doctorController.report('updateLtrRtlButtons', bug, { fontName });
  }
}

function updateBoldItalicButtons(currentFontName, $toolbarEl) {
  try {
    const font = app.fontController.findByName(currentFontName);
    if (font && font.Regular && font.default === false) {
      $toolbarEl.find('.mce-btn[aria-label="Bold"]').attr('disabled', true);
      $toolbarEl.find('.mce-btn[aria-label="Italic"]').attr('disabled', true);
    } else {
      $toolbarEl.find('.mce-btn[aria-label="Bold"]').removeAttr('disabled');
      $toolbarEl.find('.mce-btn[aria-label="Italic"]').removeAttr('disabled');
    }
  } catch (ex) {
    $toolbarEl.find('.mce-btn[aria-label="Bold"]').removeAttr('disabled');
    $toolbarEl.find('.mce-btn[aria-label="Italic"]').removeAttr('disabled');
    app.doctorController.report('updateBoldItalicButtons ', ex);
  }
}

export default {
  props: ['textOverlay', 'tabIndex', 'isActive', 'maxZIndex'],

  data: function () {
    return {
      drag: {
        xText: null,
        yText: null,
        show: false,
      },
      mode: 'view',
      clickCopy: false,
      indicationActive: false,
      indicationText: 'Copied!',
      snapTolerance: 7,
      rotateThreshold: 0.005,
    };
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setTempTextOverlay',
      'deleteTempTextOverlay',
      'setTmpPageState',
      'updateApplyBoxDesign',
      'updateApplyPosition',
      'sendToBack',
      'bringToFront',
      'setLastTextOverlayEffect',
    ]),

    syncBoxDesignCheckbox: function (style) {
      this.editorPromise.done(() => {
        setTimeout(() => {
          if (this._isDestroyed) {
            return;
          }

          const currentStyle = style || this.getTextStyle();
          const currentBox = currentStyle.box;
          const id = this.textOverlay.textStyleId;
          if (!id || id === '0') {
            return;
          }
          const savedStyle = _.find(this.story.textStyles, {
            id,
          });
          if (_.isEmpty(savedStyle)) {
            // if the stle with the saved id was deleted
            return;
          }

          const boxFromStyle = savedStyle.box;

          let doesBackgroundMatch = false;
          if (boxFromStyle.gradient) {
            doesBackgroundMatch = _.isEqual(
              currentBox.gradient,
              boxFromStyle.gradient
            );
          } else {
            doesBackgroundMatch = currentBox.bgColor === boxFromStyle.bgColor;
          }

          const boxDesignChecked =
            doesBackgroundMatch &&
            currentBox.bgGradient === boxFromStyle.bgGradient &&
            currentBox.textShadow === boxFromStyle.textShadow &&
            currentBox.animationId === currentBox.animationId &&
            currentBox.animationOutId === currentBox.animationOutId &&
            currentBox.alignment === boxFromStyle.alignment &&
            currentStyle.size.width === savedStyle.size.width;

          this.updateApplyBoxDesign({
            id: this.textOverlay.textStyleId,
            checked: boxDesignChecked,
          });
        }, 10);
      });
    },

    syncPositionCheckbox: function (style) {
      this.editorPromise.done(() => {
        const currentStyle = style || this.getTextStyle();
        const currentPosition = currentStyle.position;
        const id = this.textOverlay.textStyleId;
        if (!id || id === '0') {
          return;
        }
        const savedStyle = _.find(this.story.textStyles, { id });
        if (_.isEmpty(savedStyle)) {
          return;
        }
        const positionFromStyle = savedStyle.position;
        const positionChecked =
          currentPosition.offsetX === positionFromStyle.offsetX &&
          currentPosition.offsetY === positionFromStyle.offsetY &&
          currentPosition.rotation === positionFromStyle.rotation;

        this.updateApplyPosition({
          id: this.textOverlay.textStyleId,
          checked: positionChecked,
        });
      });
    },

    getTextStyle: function () {
      const s = {
        box: {},
        size: {},
        position: {},
        subStyles: [],
      };

      s.size.fontSize = this.textOverlay.fontSize;
      s.size.lineHeight = this.textOverlay.lineHeight;
      s.size.padding = this.textOverlay.padding;

      s.size.width = this.textOverlay.width;
      s.size.height = this.textOverlay.height;

      s.position.offsetX = this.textOverlay.offsetX;
      s.position.offsetY = this.textOverlay.offsetY;
      s.position.rotation = this.textOverlay.rotation;

      s.box.bgColor = this.textOverlay.bgColor;
      s.box.gradient = this.textOverlay.bgGradient;
      s.box.textShadow = this.textOverlay.textShadow;
      s.box.animationId = this.textOverlay.animationId;
      s.box.animationOutId = this.textOverlay.animationOutId;
      s.box.textShadowOffsetX = this.textOverlay.shadowOffsetX;
      s.box.textShadowOffsetY = this.textOverlay.shadowOffsetY;

      const tokensAndStyle = this.tokenizeHtml();
      s.subStyles = [...tokensAndStyle.subStyles];
      Object.assign(s.box, tokensAndStyle.boxStyle);
      if (s.box.direction) {
        s.box.direction = s.box.direction.toUpperCase();
      }
      return _.cloneDeep(s);
    },

    getSubStyle: function () {
      let node = getSelection().anchorNode;
      if (node && node.nodeType === 3) {
        node = node.parentElement;
      }
      const $node = $(node);
      const bold = /bold|700/i.test($node.css('font-weight'));
      const italic = $node.css('font-style') === 'italic';
      const fontFamilyCss = $node.css('font-family');
      const color = $node.css('color');
      // noinspection JSUnresolvedFunction
      const fontName = app.fontController.getRegularFontFamily(fontFamilyCss);
      const isDefault = app.fontController.isDefaultFont(fontFamilyCss);

      return { color, fontName, isDefault, bold, italic };
    },

    moveTextOverlay: function (e) {
      const arrowCodes = _.pick($.ui.keyCode, [
        'UP',
        'DOWN',
        'LEFT',
        'RIGHT',
        'DELETE',
        'BACKSPACE',
      ]);
      if (
        e.target !== e.currentTarget ||
        !_.values(arrowCodes).includes(e.which)
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (
        e.which === $.ui.keyCode.DELETE ||
        e.which === $.ui.keyCode.BACKSPACE
      ) {
        this.deleteTextOverlay(this.textOverlay);
        this.setTmpPageState({ mask: true, textOverlay: null });
      }
      const containment = $(this.$el).draggable('option', 'containment');
      const pageOffset = $(this.$el).offset();
      const x1 = containment[0];
      const x2 = containment[2];
      const y1 = containment[1];
      const y2 = containment[3];
      let offsetX = Math.round(
        this.textOverlay.offsetX * app.ASSET_EDITOR_WIDTH
      );
      let offsetY = Math.round(
        this.textOverlay.offsetY * app.ASSET_EDITOR_HEIGHT
      );

      this.drag.show = true;

      switch (e.which) {
        case arrowCodes.UP:
          if (pageOffset.top < y1) {
            return;
          }
          offsetY -= 1;
          break;
        case arrowCodes.DOWN:
          if (pageOffset.top > y2) {
            return;
          }
          offsetY += 1;
          break;
        case arrowCodes.LEFT:
          if (pageOffset.left < x1) {
            return;
          }
          offsetX -= 1;
          break;
        case arrowCodes.RIGHT:
          if (pageOffset.left > x2) {
            return;
          }
          offsetX += 1;
          break;
        default:
          return;
      }

      const ratio = 1920 / 688;
      let top = parseInt($(this.$el).css('top'));
      this.drag.yText = Math.round(top * ratio);
      let left = parseInt($(this.$el).css('left'));
      this.drag.xText = Math.round(left * ratio);

      top = Math.round(top);
      left = Math.round(left);

      this.setTmpPageState({
        markerStyle: {
          show: true,
          left: Math.min(Math.max(left, 0), app.ASSET_EDITOR_WIDTH),
          top: Math.min(Math.max(top, 0), app.ASSET_EDITOR_HEIGHT),
        },
      });

      offsetX = app.sfp(offsetX / app.ASSET_EDITOR_WIDTH);
      offsetY = app.sfp(offsetY / app.ASSET_EDITOR_HEIGHT);

      this.setTempTextOverlay({
        id: this.textOverlay.id,
        offsetX,
        offsetY,
      });
    },

    onKeyUp: function () {
      this.stopMovingTextOverlay();
    },

    onEditorKeyUp: function (e, editor) {
      const content = editor.getContent({ format: 'raw' });
      const $contentElem = $($.parseHTML(content));
      const contentText = $contentElem.text();
      const trimmed = contentText.trim();
      const emptyText = trimmed === app.ZERO_WIDTH_SPACE || _.isEmpty(trimmed);

      bus.$emit('to:isEmpty', emptyText);

      if (editor.lastSubStyle && emptyText) {
        const $el = this.createEmptyElementWithSubStyle(editor.lastSubStyle);
        editor.setContent($el[0].outerHTML, { format: 'raw' });
        this.moveCaretToEnd();
        e.preventDefault();
        e.stopPropagation();
        this.$nextTick(() => {
          this.execAlignment(editor.lastSubStyle.alignment);
        });
        return false;
      }

      if (!emptyText) {
        const leaf = this.$editor.find('[data-leaf]');
        if (leaf.length) {
          const text = leaf.text();
          if (text.startsWith(app.ZERO_WIDTH_SPACE)) {
            leaf.removeAttr('data-leaf');
            leaf[0].textContent = text.substring(1).trim();
            this.moveCaretToEnd();
          }
        }
      }
    },

    onEditorInit: function (editor) {
      this.editor = editor;
      editor.vueInstance = this;

      this.refreshText();

      if (window.getSelection) {
        if (window.getSelection().empty) {
          // Chrome
          window.getSelection().empty();
        } else if (window.getSelection().removeAllRanges) {
          // Firefox
          window.getSelection().removeAllRanges();
        }
      } else if (document.selection) {
        // IE?
        document.selection.empty();
      }
      editor.focus();

      this.editorPromise.resolve();
    },

    stopMovingTextOverlay: function () {
      this.drag.show = false;
      this.setTmpPageState({
        markerStyle: {
          show: false,
        },
      });
    },

    selectAll: function () {
      if (!this.editor || !this.editor.selection) {
        return;
      }
      this.editor.selection.select(this.editor.getBody(), true);
    },

    moveCaretToEnd: function () {
      if (!this.editor || !this.editor.selection) {
        return;
      }
      this.selectAll();
      this.editor.selection.collapse(false);
    },

    onClick: function (e) {
      const $target = $(e.target);

      const sendToBack = $target.closest('.wt-send-to-back');
      const bringToFront = $target.closest('.wt-bring-to-front');

      if (sendToBack.length || bringToFront.length) {
        bus.$emit('to:reset');
        return;
      }

      if (this.mode === 'editing') {
        if ($target.hasClass('wt-drag-handle')) {
          this.mode = 'selected';
        }
      } else if (this.mode === 'selected' || this.mode === 'view') {
        this.mode = 'editing';
      }
    },

    onDoubleClick: function (e) {
      e.stopPropagation();
      this.mode = 'editing';
    },

    isEmpty: function () {
      if (!this.$refs.editor) {
        return true;
      }
      const text = this.$editor.text().trim();
      return _.isEmpty(text) || text === app.ZERO_WIDTH_SPACE;
    },

    onCopy: function (e) {
      if (this.mode === 'selected' || this.clickCopy) {
        this.copy(e);
        e.preventDefault();
        this.clickCopy = false;
      }
    },

    onCut: function (e) {
      if (this.mode === 'selected') {
        this.copy(e);
        this.deleteTextOverlay(this.textOverlay);
        e.preventDefault();
      }
    },

    copyManually: function () {
      this.clickCopy = true;
      this.indicationActive = true;
      setTimeout(() => {
        this.indicationActive = false;
      }, 1500);
      document.execCommand('copy');
    },

    copy: function (e) {
      const html = this.editor.getContent({ format: 'raw' });
      if (html) {
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          html,
        });
      }
      Analytics.trigger('analytics:textOverlay:copyText', {
        videoID: _.isObject(this.story) && this.story.id,
      });

      const item = JSON.stringify(this.textOverlay);
      sessionStorage.setItem('application/toCopy', item);
      e.clipboardData.setData('application/toCopy', item);
    },

    onPaste: function () {
      setTimeout(() => {
        this.refreshContainment();
      }, 10);
    },

    getMaxHeight: function () {
      const modalTop = this.$modal.offset().top;
      const elTop = $(this.$el).offset().top;
      return modalTop + this.$modal.outerHeight() - elTop - 20; // 20 to avoid scrollbar, if exists
    },

    getMaxWidth: function () {
      const modalLeft = this.$modal.offset().left;
      const elLeft = $(this.$el).offset().left;
      return modalLeft + this.$modal.outerWidth() - elLeft;
    },

    initUIEffects: function () {
      this.editorPromise.done(() => {
        this.initResizable();
        this.initDraggable();
        this.initRotatable();
      });
    },

    applyPosition: function (position, height, currentHeightPromise) {
      const isBottom = (position.offsetY * 2 + height) / 2 > 0.5;
      let offsetY = position.offsetY;
      const setModelAndStyle = (offsetY) => {
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          offsetX: position.offsetX,
          offsetY,
          rotation: position.rotation,
        });
      };
      if (isBottom) {
        currentHeightPromise.done((currentHeightPercent) => {
          offsetY = position.offsetY + height - currentHeightPercent;
          setModelAndStyle(offsetY);
        });
      } else {
        setModelAndStyle(offsetY);
      }
      this.moveCaretToEnd();
    },

    revertPosition() {
      if (this.lastTextStyle) {
        this.applyPosition(
          this.lastTextStyle.position,
          this.lastTextStyle.size
        );
      }
    },

    applyBoxDesign(box, reverting) {
      const anim = _.find(
        this.themeInfo.animationPresets,
        (a) => a.id === box.animationId
      );
      if (!anim) {
        box.animationId = app.ANIMATION.NONE.id;
        box.animationOutId = app.ANIMATION.NONE.continueId;
      } else if (!box.animationOutId) {
        // last used style box has no outId
        box.animationOutId = anim.continueId;
      }

      this.setTempTextOverlay({
        id: this.textOverlay.id,
        bgColor: box.bgColor,
        bgGradient: box.gradient || box.bgGradient,
        width: box.width,
        textShadow: box.textShadow,
        shadowOffsetX: box.textShadowOffsetX,
        shadowOffsetY: box.textShadowOffsetY,
        animationId: box.animationId,
        animationOutId: box.animationOutId,
      });

      if (box.bgColor) {
        this.execCommand('boxcolorselected', box.bgColor);
      } else if (box.bgGradient || box.gradient) {
        this.execCommand('boxgradientselected', {
          gradient: box.bgGradient || box.gradient,
        });
      }

      this.execCommand('textshadowselected', {
        textShadow: box.textShadow,
      });

      if (box.direction) {
        this.execCommand('mceDirection' + box.direction, true);
      }

      if (box.allCaps) {
        this.execCommand('wttogglecase', true);
        this.refreshText();
      }
      if (reverting) {
        const { box: currentBox } = this.getTextStyle();
        if (currentBox.alignment !== box.alignment) {
          this.execAlignment(box.alignment);
        }
      } else if (
        this.lastTextStyle &&
        this.lastTextStyle.box.alignment !== box.alignment
      ) {
        this.editorPromise.done(() => {
          this.execAlignment(box.alignment);
        });
      }
      if (box.indent) {
        this.$editor.find('p').css('padding-left', box.indent + 'px');
        this.$display.find('p').css('padding-left', box.indent + 'px');
      }
      this.moveCaretToEnd();
    },

    execAlignment(alignment) {
      switch (alignment) {
        case 'RIGHT':
          this.execCommand('JustifyRight', false, true, {
            skip_focus: false,
          });
          break;
        case 'CENTER':
          this.execCommand('JustifyCenter', false, true, {
            skip_focus: false,
          });
          break;
        case 'JUSTIFY':
          this.execCommand('JustifyFull', false, true, { skip_focus: false });
          break;
        default:
          this.execCommand('JustifyLeft', false, true, { skip_focus: false });
      }
    },

    revertBoxDesign() {
      if (this.lastTextStyle) {
        this.applyBoxDesign(this.lastTextStyle.box, true);
        this.moveCaretToEnd();
      }
    },

    applySize: function (size) {
      const deferred = $.Deferred();
      this.setTempTextOverlay({
        id: this.textOverlay.id,
        lineHeight: size.lineHeight,
        fontSize: app.sfp(size.fontSize, 10),
      });

      this.execCommand('wtfontsize', app.sfp(size.fontSize, 10));
      this.execCommand('lineheightselected', size.lineHeight);

      setTimeout(() => {
        const currentHeight = $(this.$el).outerHeight();
        const currentHeightPercent = app.sfp(
          currentHeight / app.ASSET_EDITOR_HEIGHT
        );
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          height: currentHeightPercent,
        });
        deferred.resolve(currentHeightPercent);
      }, 300);
      return deferred.promise();
    },

    execCommand: function (command, value, p) {
      const params = { init: true, skip_focus: true };
      _.extend(params, p);
      this.editor.execCommand(command, false, value, params);
    },

    getRangeState: function () {
      const sel = getSelection();
      const range = document.createRange();
      range.setStart(sel.anchorNode, sel.anchorOffset);
      range.setEnd(sel.focusNode, sel.focusOffset);
      const nodes = [];
      app.namespace.Util.walkTheDOM(range.commonAncestorContainer, (node) => {
        if (node.nodeType === 3) {
          const text = node.textContent.trim();
          if (
            !(
              _.isEmpty(text) ||
              /^\s*$/.test(text) ||
              text === app.ZERO_WIDTH_SPACE
            )
          ) {
            nodes.push(node.parentNode);
          }
        }
      });
      const allBold = _.every(nodes, (node) => {
        return /bold|700/i.test($(node).css('font-weight'));
      });
      const allThin = _.every(nodes, (node) => {
        return !/bold|700/i.test($(node).css('font-weight'));
      });
      const allItalic = _.every(nodes, (node) => {
        return $(node).css('font-style') === 'italic';
      });
      const allNoneItalic = _.every(nodes, (node) => {
        return $(node).css('font-style') !== 'italic';
      });
      const nodeCount = nodes.length;
      return { allBold, allThin, allItalic, allNoneItalic, nodeCount };
    },

    applyStyle: function (style, activeSubStyle) {
      this.editorPromise.done(() => {
        this.selectAll();
        this.lastTextStyle = this.getTextStyle();
        if (style.id) {
          this.setTempTextOverlay({
            id: this.textOverlay.id,
            textStyleId: style.id,
          });
        }
        const promise = this.applySize(style.size);
        if (style.box && style.box.apply) {
          style.box.width = style.size.width;
          this.applyBoxDesign(_.cloneDeep(style.box));
        }
        if (style.position && style.position.apply) {
          this.applyPosition(style.position, style.size.height, promise);
        }
        if (style.subStyles && style.subStyles.length) {
          this.selectAll();
          this.applySubStyle(style.subStyles[activeSubStyle]);
        }
        this.editor.selection.collapse(false);
        app.stash.set('last_style', style);
      });
    },

    serializeStyle: function (box, position) {
      this.editorPromise.done(() => {
        const style = this.getTextStyle();
        style.box.apply = box;
        style.position.apply = position;
        if (!_.isEmpty(style.subStyles)) {
          app.stash.set('last_style', style);
        }
      });
    },

    applySubStyle: function (subStyle) {
      this.execCommand('forecolorselected', subStyle.color);
      this.execCommand('FontName', subStyle.fontName);

      const isDefault = app.fontController.isDefaultFont(subStyle.fontName);
      if (isDefault) {
        let rangeState = this.getRangeState();

        if (rangeState.nodeCount) {
          if (subStyle.bold) {
            let i = 0;
            while (rangeState && !rangeState.allBold && i < 3) {
              this.execCommand('mceToggleFormat', 'bold');
              rangeState = this.getRangeState();
              i++;
            }
          } else {
            let i = 0;
            while (rangeState && !rangeState.allThin && i < 3) {
              this.execCommand('mceToggleFormat', 'bold');
              rangeState = this.getRangeState();
              i++;
            }
          }
          if (subStyle.italic) {
            let i = 0;
            while (rangeState && !rangeState.allItalic && i < 3) {
              this.execCommand('mceToggleFormat', 'italic');
              rangeState = this.getRangeState();
              i++;
            }
          } else {
            let i = 0;
            while (rangeState && !rangeState.allNoneItalic && i < 3) {
              this.execCommand('mceToggleFormat', 'italic');
              rangeState = this.getRangeState();
              i++;
            }
          }
        } else {
          let node = getSelection().anchorNode;
          if (node.nodeType === 3) {
            node = node.parentNode;
          }
          const isBold = /bold|700/i.test($(node).css('font-weight'));
          const isItalic = $(node).css('font-style') === 'italic';
          if ((subStyle.bold && !isBold) || (!subStyle.bold && isBold)) {
            this.execCommand('mceToggleFormat', 'bold');
          }
          if (
            (subStyle.italic && !isItalic) ||
            (!subStyle.italic && isItalic)
          ) {
            this.execCommand('mceToggleFormat', 'italic');
          }
        }
      }
    },

    adjustPadding: function (fontSize) {
      fontSize = parseFloat(fontSize);
      const paddingTopBottom = Math.min(10, fontSize / 6);
      const lineHeight = this.$el.style.lineHeight;
      const padding = paddingStr(
        paddingTopBottom,
        fontSize * lineHeight * 0.22
      );
      this.setTempTextOverlay({
        id: this.textOverlay.id,
        padding,
      });
    },

    track: _.debounce(function (e, obj) {
      Analytics.trigger('analytics:textOverlay:' + e, obj);
    }, 500),

    createEmptyElementWithSubStyle: function (subStyle) {
      let $el = $('<i></i>');
      const $i = $el;
      if (subStyle.fontName || subStyle.color) {
        const $span = $('<span></span>');
        if (subStyle.fontName) {
          $span.addClass('font ' + subStyle.fontName);
        }
        if (subStyle.color) {
          $span.css('color', subStyle.color);
        }
        $span.wrapInner($el);
        $el = $span;
      }
      if (subStyle.isDefault) {
        if (subStyle.bold) {
          const $strong = $('<strong></strong>');
          $strong.wrapInner($el);
          $el = $strong;
        }
        if (subStyle.italic) {
          const $em = $('<em></em>');
          $em.wrapInner($el);
          $el = $em;
        }
      }
      if ($el === $i) {
        $el = $('<span data-leaf="true"></span>');
        $el.text(app.ZERO_WIDTH_SPACE);
      } else {
        $i.parent().empty().text(app.ZERO_WIDTH_SPACE).attr('data-leaf', true);
      }

      return $el;
    },

    initTinymce: function () {
      this.$refs.editor.innerHTML = this.textOverlay.html;
      this.$refs.display.innerHTML = this.textOverlay.html;
      bus.$emit('to:isEmpty', _.isEmpty(this.$editor.text().trim()));

      const pluginsAr = [
        'colorpicker',
        'textcolor',
        'wochittextshadow',
        'wochitboxcolor',
        'wochitlineheight',
        'wochitfontsize',
        'paste',
        'directionality',
        'wochittogglecase',
      ];

      const pluginsStr = pluginsAr.join(' ');

      let firstToolbarRendered = false;
      /** @namespace serverProfile.useCacheBuster */
      const versionStr =
        '?' +
        $.param({ v: serverProfile.version }) +
        (serverProfile.useCacheBuster ? '&' + $.param({ _: Date.now() }) : '');
      window.tinymce.init({
        selector: '#' + this.textOverlay.id,
        fixed_toolbar_container: '.js-tinymce-toolbar-container',
        browser_spellcheck: app.rootStore.state.user['enableSpellChecker'],
        menubar: false,
        inline: true,
        theme: 'modern',
        cache_suffix: versionStr,
        oninit: 'setPlainText',
        ontoolbarrendered: function (e) {
          const toolbar = e.target;
          const editor = toolbar.getRoot().editor;

          editor.toolbar = editor.toolbar || [];
          if (!editor.toolbarFind) {
            editor.toolbarFind = function (selector) {
              let $rv = $(); // empty set
              for (let n = 0, N = editor.toolbar.length; n < N; n++) {
                $rv = $rv.add(editor.toolbar[n].$el.find(selector));
              }
              return $rv;
            };
          }

          editor.toolbar.push(toolbar);
          if (_.isEmpty(this.textOverlay.html)) {
            const style = this._ian || app.stash.get('last_style');

            if (style && !firstToolbarRendered) {
              setTimeout(() => {
                this.applyStyle(style, 0, true);
                this.adjustPadding(style.size.fontSize);
              }, 64);
            }
            firstToolbarRendered = true;
          } else {
            if (this.textOverlay.bgColor) {
              editor.execCommand(
                'boxcolorselected',
                false,
                this.textOverlay.bgColor,
                { init: true }
              );
            } else if (this.textOverlay.bgGradient) {
              editor.execCommand(
                'boxgradientselected',
                false,
                { gradient: this.textOverlay.bgGradient },
                { init: true }
              );
            }
            editor.execCommand(
              'wtfontsize',
              false,
              app.sfp(this.textOverlay.fontSize, 10),
              { init: true, skip_focus: true }
            );
            editor.execCommand(
              'textshadowselected',
              false,
              { textShadow: this.textOverlay.textShadow },
              { init: true }
            );
          }
          // find the font drop down
          const all = toolbar.find('*');
          const $toolbarEl = $(toolbar.getEl());
          const fontFamilyControl = _.find(all, {
            type: 'listbox',
            settings: { text: 'Font Family' },
          });
          if (!_.isEmpty(fontFamilyControl)) {
            fontFamilyControl.state.on('change:value', function (e) {
              if (e && e.value) {
                updateBoldItalicButtons(e.value, $toolbarEl);
              }
            });
          }
        }.bind(this),
        convert_fonts_to_spans: true,
        plugins: pluginsStr,
        paste_as_text: true,
        toolbar: [
          'fontselect | wtfontsize | bold italic | togglecase | lineheight',
          'forecolor textshadow boxcolor | alignment outdent indent | ltr rtl',
        ],
        toolbar_items_size: 'small',
        fontsize_formats: '20 30 40 50 60 70 80 90 100 110 120 130 140 150',
        font_formats: app.fontController.fontFormats,
        formats: {
          fontname: {
            inline: 'span',
            toggle: false,
            wt_disable_child_deletion: true,
            attributes: { class: 'font %value' },
          },
        },
        setup: (editor) => {
          editor.on('keyup', (e) => {
            this.onEditorKeyUp(e, editor);
          });
          editor.on('keydown', () => {
            const text = this.$editor.text().trim();
            const isEmptyText =
              _.isEmpty(text) || text === app.ZERO_WIDTH_SPACE;
            const $p = this.$editor.find('p');
            let alignment = $p.css('text-align').toUpperCase();
            if (!['LEFT', 'RIGHT', 'CENTER', 'JUSTIFY'].includes(alignment)) {
              alignment = 'LEFT';
            }
            if (!isEmptyText) {
              editor.lastSubStyle = this.getSubStyle();
              editor.lastSubStyle.alignment = alignment;
            }
          });

          editor.on('blur', () => {
            this.setDimensions();
          });

          editor.on('init', this.onEditorInit.bind(this, editor));
          editor.on('ToggleCaseUpper', (e) => {
            if (!e.init) {
              this.refreshText();
              this.track('toggleCase', { case: 'Upper' });
            }
          });
          editor.on('ToggleCaseLower', (e) => {
            if (!e.init) {
              this.refreshText();
              this.track('toggleCase', { case: 'Lower' });
            }
          });
          editor.on('BeforeExecCommand', function (e) {
            if (!(editor.toolbar && editor.toolbar.length)) {
              return;
            }
            const $toolbarEl = $(editor.toolbar[0].getEl());
            let btn = null;
            if (e.command === 'mceToggleFormat' && e.value === 'bold') {
              btn = $toolbarEl.find('.mce-btn[aria-label="Bold"]');
              if (btn && btn.attr('disabled')) {
                e.preventDefault();
              }
            }
            if (e.command === 'mceToggleFormat' && e.value == 'italic') {
              btn = $toolbarEl.find('.mce-btn[aria-label="Italic"]');
              if (btn && btn.attr('disabled')) {
                e.preventDefault();
              }
            }
          });
          editor.on('ExecCommand', (e) => {
            this.onExecCommand(e, editor);
          });
          editor.on('nodechange', () => {
            if (_.isEmpty(editor.toolbar)) {
              return;
            }
            const $toolbarEl = $(editor.toolbar[0].getEl());
            const btnBold = $toolbarEl.find('.mce-btn[aria-label="Bold"]');
            const btnItalic = $toolbarEl.find('.mce-btn[aria-label="Italic"]');
            if (btnBold) {
              btnBold.toggleClass(
                'mce-disabled',
                !_.isUndefined(btnBold.attr('disabled'))
              );
              if (btnBold.attr('disabled')) {
                btnBold.removeClass('mce-active');
                if (editor.formatter.match('bold')) {
                  editor.formatter.toggle('bold');
                }
              }
            }
            if (btnItalic) {
              btnItalic.toggleClass(
                'mce-disabled',
                !_.isUndefined(btnItalic.attr('disabled'))
              );
              if (btnItalic.attr('disabled')) {
                btnItalic.removeClass('mce-active');
                if (editor.formatter.match('italic')) {
                  editor.formatter.toggle('italic');
                }
              }
            }
            const subStyleAtCaret = this.getSubStyle();
            this.$editor.find('p').each((i, p) => {
              if (_.isEmpty(p.style.textAlign)) {
                $(p).css('textAlign', 'left');
              }
            });
            bus.$emit('to:subStyle', subStyleAtCaret);
          });
          editor.addButton('alignment', {
            type: 'listbox',
            icon: 'alignleft',
            text: false,
            onselect: function () {
              window.tinyMCE.execCommand(this.value());
            },
            values: [
              { icon: 'alignleft', value: 'JustifyLeft', text: 'Left' },
              { icon: 'alignright', value: 'JustifyRight', text: 'Right' },
              {
                icon: 'aligncenter',
                value: 'JustifyCenter',
                text: 'Center',
              },
              {
                icon: 'alignjustify',
                value: 'JustifyFull',
                text: 'Justify',
              },
            ],
            onPostRender: function () {
              this.text('');
              const repaintToolbarOnFormatChange = () => {
                editor.formatter.formatChanged(
                  'alignleft,alignright,aligncenter,alignjustify',
                  (state, o) => {
                    if (state) {
                      this.icon(o.format);
                    }
                    this.text('');
                  }
                );
              };
              if (editor.formatter) {
                repaintToolbarOnFormatChange();
              } else {
                editor.on('init', repaintToolbarOnFormatChange);
              }
            },
          });
        },
      });
    },

    initResizable: function () {
      const snapTolerance = this.snapTolerance;

      function restorePrevPosition(instance, ui) {
        const s = instance.lastValidState;
        if (_.isEmpty(s)) {
          return;
        }
        ui.position.top = s.top;
        ui.position.left = s.left;
        ui.size.width = s.width;
        ui.size.height = s.height;
      }

      $(this.$el).resizable({
        handles: 'e, w, s, nw, ne, se, sw, n',
        start: function (event, ui) {
          const $editor = this.$editor;
          const $el = $(this.$el);
          const instance = ui.element.resizable('instance');
          const axis = instance.axis;
          const width = $el.outerWidth();
          const height = $el.outerHeight();
          const innerHeight = $editor.outerHeight();
          this.refreshContainment();

          instance._aspectRatio = axis.length > 1; // corner is dragged - axis is ne,nw, se, sw
          instance._shrinkBox =
            (axis === 'e' || axis === 'w') && height - innerHeight < 10;
          instance.originalSize.width = instance.size.width = width;
          instance.originalSize.height = instance.size.height = height;
          instance.originalSize.scrollHeight = $editor[0].scrollHeight;

          $el.css({
            height: instance.size.height,
            width: instance.size.width,
            'min-height': 'auto',
          });
          instance.lastValidState = _.extend({}, ui.position, ui.size);
          const $elsWithFont = $el.find('[style*=font-size]');
          $elsWithFont.each(function () {
            const $this = $(this);
            $this.data('font-size', $this.css('font-size'));
          });
          $editor.data('font-size', $el.css('font-size'));
          const paddingTopBottom = $editor.css('padding-top');
          const paddingLeftRight = $editor.css('padding-left');
          $editor.data({
            'padding-top-bottom': paddingTopBottom,
            'padding-left-right': paddingLeftRight,
          });
          $editor.data('text-width', this.getInnerTextWidth());
        }.bind(this),
        resize: function (event, ui) {
          const $editor = this.$editor;
          const $display = this.$display;
          const $el = $(this.$el);
          const instance = ui.element.resizable('instance');
          const axis = instance.axis;
          const isCornerDragged = axis.length > 1; // ne, nw, se, sw
          const w = ui.size.width;
          const h = ui.size.height;
          const l = ui.element.offset().left;
          const t = ui.element.offset().top;
          const r = l + w;
          const b = t + h;
          const $snapElements = $('.wt-text-overlay, .wt-to-snap'); // pleas keep the order of the selector
          let ratio;
          let fs;
          let paddingTopBottom;
          let paddingLeftRight;
          let snapped;

          if (Math.abs(this.textOverlay.rotation) < this.rotateThreshold) {
            $snapElements.filter(':visible').each(function () {
              const $this = $(this);
              const p = $this.offset();

              if ($this[0] == $el[0] || snapped) {
                return; // ever heard the tail of the snake that's eating his own tail forver?
              }
              p.right = p.left + $this.outerWidth();
              p.bottom = p.top + $this.outerHeight();
              let dw;
              let dh;

              if (t < p.top + snapTolerance && t > p.top) {
                // top to top
                dh = t - p.top;
                ui.position.top -= dh;
                ui.size.height += dh;
                snapped = true;
              } else if (l < p.left + snapTolerance && l > p.left) {
                // left to left
                dw = l - p.left;
                ui.position.left -= dw;
                ui.size.width += dw;
                snapped = true;
              } else if (r > p.right - snapTolerance && r < p.right) {
                // right to right
                dw = p.right - r;
                ui.size.width += dw;
                snapped = true;
              } else if (b > p.bottom - snapTolerance && b < p.bottom) {
                // bottom to bottom
                dh = p.bottom - b;
                ui.size.height += dh;
                snapped = true;
              } else if (l < p.right + snapTolerance && l > p.right) {
                // left to right
                dw = l - p.right;
                ui.position.left -= dw;
                ui.size.width += dw;
                snapped = true;
              } else if (r > p.left - snapTolerance && r < p.left) {
                // right to left
                dw = p.left - r;
                ui.size.width += dw;
                snapped = true;
              } else if (t < p.bottom + snapTolerance && t > p.bottom) {
                // top to bottom
                dh = t - p.bottom;
                ui.position.top -= dh;
                ui.size.height += dh;
                snapped = true;
              } else if (b > p.top - snapTolerance && b < p.top) {
                // bottom to top
                dh = p.top - b;
                ui.position.top += dh;
                ui.size.height += dh;
                snapped = true;
              }
            });
          }

          if (
            this.isIllegalOffset(ui) ||
            this.isIllegalWidth(ui, isCornerDragged) ||
            this.isIllegalHeight(
              ui,
              isCornerDragged,
              instance.originalSize.scrollHeight
            )
          ) {
            restorePrevPosition(instance, ui);
          } else {
            instance.lastValidState = _.extend({}, ui.position, ui.size);
          }

          if (isCornerDragged) {
            ratio = ui.size.width / ui.originalSize.width;
            fs = parseFloat($editor.data('font-size'));
            const newFontSize = app.sfp(fs * ratio, 10);
            if (newFontSize) {
              this.fontSize = newFontSize;
            }
            this.editor.execCommand('wtfontsize', false, newFontSize + 'px', {
              skip_focus: true,
            });
            paddingTopBottom = app.sfp(
              parseFloat($editor.data('padding-top-bottom')) * ratio - 1,
              100
            );
            paddingLeftRight = app.sfp(
              parseFloat($editor.data('padding-left-right')) * ratio - 1,
              100
            );
            const padding = paddingStr(paddingTopBottom, paddingLeftRight);
            $editor.add($display).css('padding', padding);
            this.setTempTextOverlay({
              id: this.textOverlay.id,
              padding,
            });
          }

          const maxHeight = this.getMaxHeight();
          const maxWidth = this.getMaxWidth();

          if (instance._shrinkBox) {
            $el.css({
              height: 'auto',
              'min-height': Math.min(ui.size.height, $editor.outerHeight()),
            });
          } else {
            $el.css({
              height: 'auto',
              'min-height': ui.size.height,
            });
          }

          $el.css('max-height', maxHeight);
          $el.css('max-width', maxWidth);
        }.bind(this),
        stop: function (event, ui) {
          const $editor = this.$editor;
          const $el = $(this.$el);
          const instance = ui.element.resizable('instance');
          const positionHelper =
            Math.abs(this.textOverlay.rotation) < this.rotateThreshold
              ? ui.element.position()
              : ui.position;
          this.refreshContainment();
          if (instance._shrinkBox) {
            $el.css({
              height: 'auto',
              'min-height': Math.min(ui.size.height, $editor.outerHeight()),
            });
          } else {
            $el.css({
              height: 'auto',
              'min-height': ui.size.height,
            });
          }
          this.setTempTextOverlay({
            id: this.textOverlay.id,
            offsetX: app.sfp(positionHelper.left / app.ASSET_EDITOR_WIDTH),
            offsetY: app.sfp(positionHelper.top / app.ASSET_EDITOR_HEIGHT),
          });
          this.setDimensions();
          this.matchToWithOtherBorders();
          this.serializeStyle(true, false);
        }.bind(this),
      });
    },
    disableSnap: function () {
      const instance = $(this.$el).draggable('instance');
      if (!instance) {
        return;
      }
      $(this.$el).draggable('option', 'snap', false);
    },

    /**
     * Following method intended for pixel-perfect asset offsets,
     * looks for pixel-collisions with other TOs and fixes the margins to 0
     */
    matchToWithOtherBorders: function () {
      console.log('matchToWithOtherBorders - new offset is set!');
      let offsetY = this.textOverlay.offsetY;
      let offsetX = this.textOverlay.offsetX;
      const toHeight = this.textOverlay.height;
      const toBottom = this.textOverlay.offsetY + this.textOverlay.height;
      const toRight = this.textOverlay.offsetX + this.textOverlay.width;
      const toWidth = this.textOverlay.width;
      const rotation = Math.abs(this.textOverlay.rotation);
      const verticalCollisionOffset = 0.0075;
      const horizontalCollisionOffset = 0.0095;

      if (!_.isEmpty(this.textOverlays) && rotation < this.rotateThreshold) {
        this.textOverlays.forEach((tmpTextOverlay) => {
          if (
            tmpTextOverlay.id === this.textOverlay.id ||
            tmpTextOverlay.animationStartTime >
              this.textOverlay.animationEndTime ||
            tmpTextOverlay.animationEndTime <
              this.textOverlay.animationStartTime ||
            Math.abs(tmpTextOverlay.rotation) > this.rotateThreshold
          ) {
            return;
          }

          const tmpToX = tmpTextOverlay.offsetX;
          const tmpToTop = tmpTextOverlay.offsetY;
          const tmpToBottom = tmpTextOverlay.offsetY + tmpTextOverlay.height;
          const tmpToRight = tmpTextOverlay.offsetX + tmpTextOverlay.width;
          const tmpToHeight = tmpTextOverlay.offsetY + tmpTextOverlay.height;

          if (
            (offsetY >= tmpToTop && offsetY <= tmpToHeight) ||
            (toBottom >= tmpToTop && toBottom <= tmpToHeight)
          ) {
            // vertical match...
            if (
              (tmpToRight > offsetX &&
                offsetX > tmpToRight - horizontalCollisionOffset) ||
              (tmpToRight < offsetX &&
                offsetX < tmpToRight + horizontalCollisionOffset)
            ) {
              offsetX = tmpToRight;
            }
            if (
              (tmpToX > toRight &&
                toRight > tmpToX - horizontalCollisionOffset) ||
              (tmpToX < toRight && toRight < tmpToX + horizontalCollisionOffset)
            ) {
              offsetX = tmpToX - toWidth;
            }
          }

          if (
            (tmpToBottom > offsetY &&
              offsetY > tmpToBottom - verticalCollisionOffset) ||
            (tmpToBottom < offsetY &&
              offsetY < tmpToBottom + verticalCollisionOffset)
          ) {
            // bottom stickiness
            console.log('bottom stickiness');
            offsetY = tmpToBottom;
          } else if (
            (tmpToTop > toBottom &&
              toBottom > tmpToTop - verticalCollisionOffset) ||
            (tmpToTop < toBottom &&
              toBottom < tmpToTop + verticalCollisionOffset)
          ) {
            // top stickiness
            console.log('top stickiness');
            offsetY = tmpToTop - toHeight;
          }
          console.log(tmpToBottom, offsetY, tmpToTop, toBottom);
        });

        if (
          offsetY != this.textOverlay.offsetY ||
          offsetX != this.textOverlay.offsetX
        ) {
          this.$nextTick(function () {
            this.setTempTextOverlay({
              id: this.textOverlay.id,
              offsetX,
              offsetY,
            });
          });
        }
      }
    },

    enableSnap: function () {
      const instance = $(this.$el).draggable('instance');
      if (!instance) {
        return;
      }
      $(this.$el).draggable('option', 'snap', '.wt-to-snap, .wt-text-overlay');
    },

    initRotatable: function () {
      $(this.$el).rotatable({
        stop: function (event, ui) {
          const rotation = app.sfp(ui.angle.current, 1000);
          this.setTempTextOverlay({
            id: this.textOverlay.id,
            rotation: rotation,
          });
          if (Math.abs(rotation) < this.rotateThreshold) {
            this.enableSnap();
          } else {
            this.disableSnap();
          }
          this.track('rotate');
        }.bind(this),
        angle: this.textOverlay.rotation,
      });
    },

    initDraggable: function () {
      const offset = this.$modal.offset();
      const borderWidth = parseInt($(this.$el).css('border-width')) * 2;
      const containment = [
        offset.left,
        offset.top,
        offset.left +
          this.$modal.width() -
          $(this.$el).width() -
          borderWidth -
          20,
        offset.top +
          this.$modal.height() -
          $(this.$el).height() -
          borderWidth -
          20,
      ];

      $(this.$el).draggable({
        handle: false,
        snap:
          Math.abs(this.textOverlay.rotation) < this.rotateThreshold
            ? '.wt-to-snap, .wt-text-overlay'
            : false,
        snapMode: 'both',
        snapTolerance: this.snapTolerance,
        containment: containment,
        scroll: false,
        start: function () {
          this.drag.show = true;

          const id = this.textOverlay.textStyleId;
          if (!id) {
            return;
          }
          this.syncPositionCheckbox();
        }.bind(this),
        drag: function (event, ui) {
          const ratio = 1920 / 688;
          const topLabel = Math.round(ui.helper.position().top * ratio);
          const leftLabel = Math.round(ui.helper.position().left * ratio);
          this.drag.yText = topLabel;
          this.drag.xText = leftLabel;
          this.setTmpPageState({
            markerStyle: {
              show: true,
              left: Math.min(ui.helper.position().left, app.ASSET_EDITOR_WIDTH),
              top: Math.min(ui.helper.position().top, app.ASSET_EDITOR_HEIGHT),
            },
          });
        }.bind(this),
        stop: function (e, ui) {
          const positionHelper =
            Math.abs(this.textOverlay.rotation) < this.rotateThreshold
              ? ui.helper.position()
              : ui.position;
          this.setTempTextOverlay({
            id: this.textOverlay.id,
            offsetX: app.sfp(positionHelper.left / app.ASSET_EDITOR_WIDTH),
            offsetY: app.sfp(positionHelper.top / app.ASSET_EDITOR_HEIGHT),
          });
          this.matchToWithOtherBorders();
          this.setTmpPageState({
            markerStyle: {
              show: false,
            },
          });
          this.drag.show = false;
          this.mode = 'selected';
          $(this.$el).css('max-height', this.getMaxHeight() + 'px');
          this.refreshContainment();
        }.bind(this),
      });
    },

    refreshContainment: function (e) {
      const $editor = this.$editor;
      const $el = $(this.$el);
      e && e.stopPropagation();
      const offset = this.$modal.offset();
      const instance = $el.draggable('instance');
      if (!instance) {
        return;
      }
      const borderWidth = parseInt($el.css('border-width')) * 2;
      this.setTempTextOverlay({
        id: this.textOverlay.id,
        _text: $editor.text(),
      });
      $el.draggable('option', 'containment', [
        offset.left,
        offset.top,
        offset.left + this.$modal.width() - $el.width() - borderWidth - 20,
        offset.top + this.$modal.height() - $el.height() - borderWidth - 0,
      ]);
    },

    getInnerTextWidth: function () {
      const paragraphs = this.tokenizeHtml().paragraphs;
      const widths = paragraphs.map((p) => {
        const widths = p.tokens.map((t) => t.right - t.left);
        return Math.max(...widths);
      });
      return Math.max(...widths) * app.ASSET_EDITOR_WIDTH;
    },

    tokenizeHtml: function (updateStore) {
      if (!this.editor) {
        app.doctorController.report(
          'trying to tokenize html, but editor is not available'
        );
        // TODO: when it happens investigate why and decide what to return
        // cause currently causes unhandled error
        return {};
      }

      const $clone = $(this.$el).clone();
      $clone.find('.wt-text-overlay-editor').siblings().remove();
      $clone.find('.wt-text-overlay-editor').addClass('tokenizer-root');
      const tokensAndStyle = app.namespace.Util.tokenizeHtml($clone);
      const html = this.editor.getContent({ format: 'raw' });
      if (updateStore) {
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          paragraphs: tokensAndStyle.paragraphs,
          html,
        });
      }
      return tokensAndStyle;
    },

    isIllegalOffset: function (ui) {
      const modalOffset = this.$modal.offset();
      const assetOffset = this.$stage.offset();
      const minTop = modalOffset.top - assetOffset.top;
      const minLeft = modalOffset.left - assetOffset.left;
      const maxBottom =
        modalOffset.top + this.$modal.outerHeight() - assetOffset.top;
      const maxRight =
        modalOffset.left + this.$modal.outerWidth() - assetOffset.left;
      return (
        ui.position.top < minTop ||
        ui.position.left < minLeft ||
        ui.position.top + ui.size.height > maxBottom ||
        ui.position.left + ui.size.width > maxRight
      );
    },

    isIllegalWidth: function (ui, isCornerDragged) {
      const $editor = this.$editor;
      const textWidth = $editor.data('text-width');
      const padding = parseFloat($editor.css('padding-left'));
      return (
        ui.size.width < 20 ||
        (textWidth + 2 * padding > ui.size.width &&
          ui.originalSize.width > ui.size.width &&
          !isCornerDragged)
      );
    },

    isIllegalHeight: function (ui, isCornerDragged, scrollHeight) {
      return (
        ui.size.height < 20 ||
        (scrollHeight + 5 > ui.size.height &&
          ui.originalSize.height > ui.size.height &&
          !isCornerDragged)
      );
    },

    setDimensions: function () {
      const $el = $(this.$el);
      this.setTempTextOverlay({
        id: this.textOverlay.id,
        width: app.sfp($el.outerWidth() / app.ASSET_EDITOR_WIDTH),
        height: app.sfp($el.outerHeight() / app.ASSET_EDITOR_HEIGHT),
      });
    },

    onExecCommand: function (e, editor) {
      const $el = $(this.$el);
      const text = this.$editor.text().trim();
      const emptyText = text === app.ZERO_WIDTH_SPACE || _.isEmpty(text);
      const commandsThatLeaveTextStyle = [
        'textshadowselected',
        'boxcolorselected',
        'boxgradientselected',
        'JustifyLeft',
        'JustifyRight',
        'JustifyCenter',
        'JustifyFull',
        'Indent',
        'Outdent',
        'mceDirectionLTR',
        'mceDirectionRTL',
      ];
      const ignoredCommands = ['SelectAll', 'mceRepaint', 'mceInsertContent'];
      // console.log('command: %c' + e.command, 'color: blue', ' value: ' +
      //     (typeof e.value === 'object' ? JSON.stringify(e.value) : e.value));

      if (
        !e.init &&
        !ignoredCommands.includes(e.command) &&
        commandsThatLeaveTextStyle.includes(e.command)
      ) {
        this.$nextTick(() => {
          this.lastTextStyle = this.getTextStyle();
          this.syncBoxDesignCheckbox();
        });
      }

      let width;
      let scrollWidth;

      if (!editor.vueInstance) {
        return;
      }

      const setEmptyContent = () => {
        const $el = this.createEmptyElementWithSubStyle(editor.lastSubStyle);
        editor.setContent($el[0].outerHTML, { format: 'raw' });
        this.moveCaretToEnd();
      };

      if (
        e.command === 'forecolorselected' &&
        emptyText &&
        editor.lastSubStyle
      ) {
        editor.lastSubStyle.color = e.value;
        setEmptyContent();
      }

      if (e.command === 'mceToggleFormat' && emptyText && editor.lastSubStyle) {
        const $toolbarEl = $(editor.toolbar[0].getEl());
        if (e.value === 'bold') {
          editor.lastSubStyle.bold = $toolbarEl
            .find('.mce-btn[aria-label="Bold"]')
            .hasClass('mce-active');
          setEmptyContent();
        }
        if (e.value === 'italic') {
          editor.lastSubStyle.italic = $toolbarEl
            .find('.mce-btn[aria-label="Italic"]')
            .hasClass('mce-active');
          setEmptyContent();
        }
      }

      if (e.command !== 'mceRepaint' && !e.init) {
        this.track('textOverlayCommand', {
          command: e.command,
          value: e.value,
        });
      }

      if (e.command === 'boxgradientselected') {
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          bgColor: null,
          bgGradient: e.value.gradient,
        });
      }

      if (e.command === 'boxcolorselected') {
        const vizFormat = e.value.replace(/,/g, ', ');
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          bgColor: vizFormat,
          bgGradient: null,
        });
      }

      if (e.command === 'textshadowselected') {
        const obj = e.value;
        const textShadowWizFormat = obj.textShadow.replace(/,/g, ', ');
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          textShadow: textShadowWizFormat,
          shadowOffsetX: app.sfp(obj.shadowOffsetX / app.ASSET_EDITOR_WIDTH),
          shadowOffsetY: app.sfp(obj.shadowOffsetY / app.ASSET_EDITOR_HEIGHT),
        });
      }

      if (e.command === 'wtfontsize' && editor.vueInstance) {
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          fontSize: app.sfp(parseFloat(e.value), 10),
        });
        width = $el.outerWidth();
        scrollWidth = this.$refs.editor.scrollWidth;
        this.refreshContainment();
        this.adjustPadding(e.value);
        if (scrollWidth > width) {
          $el.width(scrollWidth + parseFloat(this.$editor.css('padding-left')));
        }
      }
      if (e.command === 'lineheightselected') {
        this.refreshContainment();
        this.adjustPadding(this.textOverlay.fontSize);
        this.setTempTextOverlay({
          id: this.textOverlay.id,
          lineHeight: e.value,
        });
      }
      if (e.command === 'FontName' && editor.vueInstance) {
        const $toolbarEl = $(editor.toolbar[0].getEl());
        const fontName = e.value;
        updateBoldItalicButtons(fontName, $toolbarEl);
        updateLtrRtlButtons(fontName, editor);
        const $currentNode = $(e.target.selection.getNode());
        $currentNode.css('font-family', '');
        const $font = $currentNode.find('font');
        if ($font.length) {
          $font.removeAttr('face');
        }
      }

      if (e.command === 'mceInsertContent') {
        const subStyle = this.getSubStyle();
        this.applySubStyle(subStyle);
        this.editor.selection.collapse(false);
      }
    },

    setTextStyleId: function (textStyleId) {
      this.setTempTextOverlay({
        id: this.textOverlay.id,
        textStyleId,
      });
    },

    refreshText: function () {
      this.setTempTextOverlay({
        id: this.textOverlay.id,
        _text: this.$editor.text(),
      });
    },

    getBackgroundColor: function () {
      if (this.textOverlay.bgColor) {
        return this.textOverlay.bgColor;
      } else if (this.textOverlay.bgGradient) {
        return app.namespace.Util.gradientModelToCss(
          this.textOverlay.bgGradient
        );
      }
    },

    getTextBorder: function () {
      if (this.textOverlay.bgColor) {
        return 'transparent 2px solid';
      } else if (this.textOverlay.bgGradient) {
        return this.getBackgroundColor();
      }
    },

    getTextShadowStr: function () {
      // if textShadow is none (disable), or for backwards compatibility
      // if there's no shadowOffsetX and shadowOffsetY, return original textShadow.
      if (
        this.textOverlay.textShadow === 'none' ||
        (!this.textOverlay.shadowOffsetX && !this.textOverlay.shadowOffsetY)
      ) {
        return this.textOverlay.textShadow;
      }

      // construct textShadow css from props
      const rgba = this.textOverlay.textShadow.match(/.+\)/)[0];
      const xPx =
        app.sfp(this.textOverlay.shadowOffsetX * app.ASSET_EDITOR_WIDTH) + 'px';
      const yPx =
        app.sfp(this.textOverlay.shadowOffsetY * app.ASSET_EDITOR_HEIGHT) +
        'px';
      // the last value in the array is the blur
      const blur = this.textOverlay.textShadow.match(/\S+/g).slice(-1)[0];

      return `${rgba} ${xPx} ${yPx} ${blur}`;
    },

    deleteTextOverlay(textOverlay) {
      this.deleteTempTextOverlay(textOverlay.id);
      bus.$emit('to:deleted', textOverlay);
    },

    clickedSendToBack() {
      if (this.sendToBackDisabled) {
        return;
      }
      this.sendToBack(this.textOverlay.id);
    },

    clickedBringToFront() {
      if (this.bringToFrontDisabled) {
        return;
      }
      this.bringToFront(this.textOverlay.id);
    },
  },

  computed: {
    ...mapGetters('assetEditor', [
      'timeInAsset',
      'story',
      'themeInfo',
      'tmpAGD',
    ]),

    wrapperStyleObject: function () {
      const backgroundType = this.textOverlay.bgGradient
        ? 'background-image'
        : 'background';

      const style = {
        zIndex:
          this.mode !== 'view' || this.drag.show
            ? this.maxZIndex + 21
            : this.textOverlay.zIndex + 20,
        textShadow: this.getTextShadowStr(),
        [backgroundType]: this.getBackgroundColor(),
        // border: this.getTextBorder(),
        minHeight:
          app.sfp(this.textOverlay.height * app.ASSET_EDITOR_HEIGHT) + 'px',
        width: app.sfp(this.textOverlay.width * app.ASSET_EDITOR_WIDTH) + 'px',
        left: app.sfp(this.textOverlay.offsetX * app.ASSET_EDITOR_WIDTH) + 'px',
        top: app.sfp(this.textOverlay.offsetY * app.ASSET_EDITOR_HEIGHT) + 'px',
        lineHeight: this.textOverlay.lineHeight,
        fontSize: app.sfp(this.textOverlay.fontSize, 10) + 'px',
        transform: 'rotate(' + this.textOverlay.rotation + 'rad)',
      };

      if (this.textOverlay.bgGradient) {
        style.borderImage = this.getBackgroundColor() + ' 1';
      }

      return style;
    },

    wrapperClassObject: function () {
      return {
        'to-editing': this.isActive && this.mode === 'editing',
        'to-selected': this.isActive && this.mode === 'selected',
        'ui-draggable-dragging': this.drag.show,
      };
    },

    editorStyleObject: function () {
      return {
        padding: this.textOverlay.padding,
      };
    },

    animationPresets: function () {
      return this.themeInfo.animationPresets;
    },

    language: function () {
      return this.story.programmer.language;
    },

    isVisible: function () {
      return (
        this.textOverlay.animationStartTime <= this.timeInAsset &&
        this.textOverlay.animationEndTime >= this.timeInAsset
      );
    },

    textOverlays: function () {
      return this.tmpAGD.textOverlays;
    },

    bringToFrontDisabled() {
      return this.textOverlay.zIndex === this.maxZIndex;
    },

    sendToBackDisabled() {
      return this.textOverlay.zIndex === 0;
    },
  },

  beforeDestroy: function () {
    const $el = $(this.$el);
    if ($el.hasClass('ui-resizable')) {
      $el.resizable('destroy');
    }
    if ($el.hasClass('ui-draggable')) {
      $el.draggable('destroy');
    }
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
      this.setTmpPageState({ mask: true, textOverlay: null });
    }
  },

  watch: {
    isActive: function (isActive) {
      if (isActive) {
        this.editorPromise.done(() => {
          this.editor.focus();
        });
      }
    },

    mode: function (mode, prevMode) {
      const $el = $(this.$el);
      const instance = $el.draggable('instance');

      if (prevMode === 'editing') {
        this.serializeStyle(true, false);
      }

      if (mode === 'editing') {
        if (this.themeInfo.showFreeText !== false) {
          this.setLastTextOverlayEffect(this.textOverlay);
        }
        instance && $el.draggable('option', 'handle', '.wt-drag-handle');
        bus.$emit('to:editing', this.textOverlay);
        this.editorPromise.done(() => {
          this.$nextTick(() => {
            this.editor.fire('focusin');
            this.editor.focus();
            this.moveCaretToEnd();
          });
        });
      } else if (mode === 'selected') {
        instance && $el.draggable('option', 'handle', false);
        bus.$emit('to:selected', this.textOverlay);
        if (prevMode === 'editing') {
          this.$display.html(this.$editor.html());
          this.serializeStyle(true, false);
        }
        this.editor && this.editor.fire('focusout');
        this.serializeStyle(true, false);
      } else {
        // 'view'
        instance && $el.draggable('option', 'handle', false);
        this.editor && this.editor.fire('focusout');
        this.$display.html(this.$editor.html());
      }
    },
  },

  mounted: function () {
    this.$editor = $(this.$refs.editor);
    this.$display = $(this.$refs.display);
    const $el = $(this.$el);
    this.editorPromise = $.Deferred();
    const text = this.$editor.text();
    this.setTempTextOverlay({
      id: this.textOverlay.id,
      _text: text,
    });
    this.$modal = $el.closest('.wt-asset-editor-middle');
    this.$stage = $el.closest('.wt-asset-editor-stage');
    this.initTinymce();
    this.initUIEffects();
    $el.css('max-height', this.getMaxHeight() + 'px');
  },
};
</script>
